import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "./supabase";
import { type User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { API_BASE_URL } from "./api";

type AuthCtx = {
  user: User | null;
  role: "customer" | "admin" | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<"customer" | "admin" | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (accessToken: string) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000); // 4s max wait
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (res.ok) {
        const profile = await res.json();
        setRole(profile.role);
      } else {
        setRole("customer");
      }
    } catch (err: any) {
      clearTimeout(timer);
      if (err?.name === "AbortError") {
        console.error("[AuthProvider] Profile fetch TIMED OUT after 4s (backend cold start?) - defaulting to customer");
      } else {
        console.error("[AuthProvider] Profile fetch error:", err?.message ?? err);
      }
      setRole("customer"); // fallback on any error/timeout
    }
  };

  useEffect(() => {
    let mounted = true;
    // Safety net: always unblock UI within 2s even if Supabase/backend hangs
    const safetyTimeout = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 2000);

    // 1. Get initial session
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        clearTimeout(safetyTimeout);
        if (session) {
          setUser(session.user);
          localStorage.setItem("supabase.auth.token", session.access_token);
          // Don't block UI on profile fetch — run it in background
          fetchProfile(session.access_token).finally(() => {
            if (mounted) setLoading(false);
          });
        } else {
          setUser(null);
          setRole(null);
          localStorage.removeItem("supabase.auth.token");
          if (mounted) setLoading(false);
        }
      })
      .catch((err) => {
        console.error("[AuthProvider] getSession() error:", err?.message ?? err);
        if (mounted) setLoading(false);
      });

    // 2. Listen to auth changes — do NOT set loading=true here, it freezes the UI
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      // Update user state silently without blocking UI
      if (session) {
        setUser(session.user);
        localStorage.setItem("supabase.auth.token", session.access_token);
        fetchProfile(session.access_token).catch((err) => {
          console.error("[AuthProvider] onAuthStateChange fetchProfile failed:", err?.message ?? err);
          setRole("customer");
        });
      } else {
        setUser(null);
        setRole(null);
        localStorage.removeItem("supabase.auth.token");
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      setUser(null);
      setRole(null);
      localStorage.removeItem("supabase.auth.token");
      toast.success("Signed out successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to sign out");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, role, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
